import { useState } from 'react';
import {
  FileText, FolderOpen, Settings,
  LogOut, LogIn, Share2, Shield, CreditCard, Download, Link, Send, HelpCircle,
  Landmark, BarChart2, TrendingUp, Receipt, ChevronDown,
  GitFork, LayoutDashboard, Sparkles
} from 'lucide-react';
import { TIER_NAMES } from '../config/tierDefaults';
import { T, LogoMark, Wordmark, amountStyle } from '../theme';
import { formatAmount } from '../utils/formatters';
import pkg from '../../package.json';

export const NAV_BTN = {
  display: 'flex', alignItems: 'center', gap: 10,
  width: '100%', padding: '13px 16px', borderRadius: 14,
  border: 'none', fontSize: 14, fontWeight: 700,
  cursor: 'pointer', color: '#fff', textAlign: 'left',
  background: 'rgba(255,255,255,0.15)',
};

const FINANCE_VIEWS = [
  // { key: 'dashboard', label: 'Home' },
  { key: 'combined',  label: 'My Finances' },
  { key: 'financial', label: 'Financial Summary' },
];

export function FinanceViewNav({
  current,
  onGoToDashboard,
  onGoToFinances, onGoToFinancial, onGoToTax, onGoToExpenses,
  onImportCreditCard, onImportBank,
}) {
  const handlers = { dashboard: onGoToDashboard, combined: onGoToFinances, financial: onGoToFinancial, tax: onGoToTax, expenses: onGoToExpenses };
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', paddingTop: 10, marginTop: 6, borderTop: '1px solid rgba(255,255,255,0.12)' }}>
     <button onClick={onGoToDashboard} 
     style={{ padding: '5px 10px',  verticalAlign: 'middle', fontSize: 15, fontWeight: 700, borderRadius: 4, cursor: 'pointer',  color: T.tealBright,
      background: 'transparent', border: '1px solid rgba(255,255,255,0.25)', display: 'flex', alignItems: 'center', gap: 4 }}>
      <LayoutDashboard size={18} />
      </button>
      {FINANCE_VIEWS.map(({ key, label }) => (
        <button
          key={key}
          onClick={current !== key ? handlers[key] : undefined}
          style={{
            padding: '4px 10px', borderRadius: 16, fontSize: 11, fontWeight: 700,
            border: 'none', cursor: current === key ? 'default' : 'pointer',
            background: current === key ? 'rgba(255,255,255,0.92)' : 'rgba(255,255,255,0.15)',
            color: current === key ? T.navy : 'rgba(255,255,255,0.85)',
          }}
        >
          {label}
        </button>
      ))}
      <div style={{ flex: 1 }} />
      {onImportCreditCard && (
        <button
          onClick={onImportCreditCard}
          style={{ padding: '4px 10px', borderRadius: 16, fontSize: 11, fontWeight: 700, border: '1px solid rgba(255,255,255,0.25)', cursor: 'pointer', background: 'transparent', color: 'rgba(255,255,255,0.75)', display: 'flex', alignItems: 'center', gap: 4 }}
        >
          <CreditCard size={11} /> Import CC
        </button>
      )}
      {onImportBank && (
        <button
          onClick={onImportBank}
          style={{ padding: '4px 10px', borderRadius: 16, fontSize: 11, fontWeight: 700, border: '1px solid rgba(255,255,255,0.25)', cursor: 'pointer', background: 'transparent', color: 'rgba(255,255,255,0.75)', display: 'flex', alignItems: 'center', gap: 4 }}
        >
          <Landmark size={11} /> Import Bank
        </button>
      )}
    </div>
  );
}

export function TierAdminBadges({ tier, isAdmin, onAdminClick }) {
  return (
    <>
      {tier && (
        <div style={{
          fontSize: 10, fontWeight: 800, padding: '2px 7px', borderRadius: 10,
          background: 'rgba(255,255,255,0.18)', color: '#fff', letterSpacing: 0.3,
        }}>
          {(TIER_NAMES[tier] || tier).toUpperCase()}
        </div>
      )}
      {isAdmin && onAdminClick && (
        <button
          onClick={onAdminClick}
          title="Admin Panel"
          style={{ background: 'rgba(255,255,255,0.18)', border: 'none', borderRadius: 6, padding: '3px 6px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
        >
          <Shield size={12} color="#fff" />
        </button>
      )}
    </>
  );
}

export function ReportBar({ currentFileName, autoSaving, currentFileAccess, onShareClick, onLoadClick, onCreateReviewLinkClick }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div
        onClick={() => onLoadClick?.()}
        style={{
          display: 'flex', alignItems: 'center', gap: 4,
          fontSize: 15, color: '#fff', fontWeight: 600, letterSpacing: 0.3,
          cursor: onLoadClick ? 'pointer' : 'default',
          ...(onLoadClick ? {
            border: '1.5px solid rgba(255,255,255,0.3)',
            borderRadius: 7,
            padding: '3px 8px 3px 10px',
            background: 'rgba(255,255,255,0.08)',
          } : {}),
        }}
      >
        {currentFileName || 'No report open'}
        {onLoadClick && <ChevronDown size={13} strokeWidth={2.5} style={{ opacity: 0.65, flexShrink: 0 }} />}
      </div>
      {autoSaving && (
        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.55)', fontStyle: 'italic' }}>saving…</div>
      )}
      {currentFileAccess === 'view' && (
        <div style={{ fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.6)', background: 'rgba(0,0,0,0.2)', borderRadius: 4, padding: '2px 6px', letterSpacing: 0.3 }}>
          View only
        </div>
      )}
      {currentFileName && onShareClick && (
        <button
          onClick={() => onShareClick({ name: currentFileName })}
          style={{
            display: 'flex', alignItems: 'center', gap: 4,
            background: 'rgba(255,255,255,0.15)', border: 'none',
            borderRadius: 6, padding: '3px 8px', cursor: 'pointer',
            color: '#fff', fontSize: 11, fontWeight: 700,
          }}
        >
          <Share2 size={11} /> Share
        </button>
      )}
      {currentFileName && onCreateReviewLinkClick && (
        <button
          onClick={() => onCreateReviewLinkClick({ name: currentFileName })}
          style={{
            display: 'flex', alignItems: 'center', gap: 4,
            background: 'rgba(255,255,255,0.15)', border: 'none',
            borderRadius: 6, padding: '3px 8px', cursor: 'pointer',
            color: '#fff', fontSize: 11, fontWeight: 700,
          }}
        >
          <Link size={11} /> Live Link
        </button>
      )}
    </div>
  );
}

// amountSize=32 for Header, 24 for Sidebar — other sizes derived from it
export function SpendBlock({
  totalSpend, defaultCurrency, adjustmentFactor,
  ratesLoading, ratesError = null, expenses, user,
  tier, scansUsed, totalScansAllowed,
  amountSize = 24,
}) {
  const adjNum     = parseFloat(adjustmentFactor) || 0;
  const countSize  = amountSize > 28 ? 13 : 18;
  const countColor = amountSize > 28 ? 'rgba(255,255,255,0.6)' : 'rgba(255,255,255,0.55)';

  return (
    <>
      <div style={{ fontSize: amountSize, fontWeight: 900, color: '#fff', lineHeight: 1.1, ...amountStyle({ letterSpacing: amountSize > 28 ? -1 : -0.5 }) }}>
        {ratesLoading
          ? <span style={{ fontSize: Math.round(amountSize * 0.75), opacity: 0.7 }}>Converting…</span>
          : formatAmount(totalSpend, defaultCurrency)
        }
      </div>
      {adjNum !== 0 && (
        <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: 10 }}>
          non-{defaultCurrency} adjusted {adjNum > 0 ? '+' : ''}{adjNum}%
        </span>
      )}
      {ratesError && (
        <div style={{ fontSize: 11, color: 'rgba(255,200,100,0.9)', marginTop: 2 }}>
          ⚠ Exchange rates unavailable — amounts shown unconverted
        </div>
      )}
      <div style={{ fontSize: countSize, color: countColor, marginTop: 4 }}>
        {expenses.length} transaction{expenses.length !== 1 ? 's' : ''} tracked
        {!user && <span> · local only</span>}
      </div>
      {user && tier && totalScansAllowed != null && (
        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginTop: 3 }}>
          {scansUsed}/{totalScansAllowed === Infinity ? '∞' : totalScansAllowed} scans this month
        </div>
      )}
    </>
  );
}

// onClose: pass the menu-close fn from Header; omit in Sidebar (buttons stay as-is)
export function NavMenu({
  onClose, user,
  onNewReport, onLoadClick, onExportClick, onPrefsClick,
  onCreateReviewLinkClick, onReviewLinksClick,
  onBillingClick, onInstallClick, onHelpClick, onLogout, onLogin,
  onImportCreditCard, onImportBank, onBusinessSummary, onFinancialSummary, onTaxSummary,
}) {
  const w = cb => onClose ? () => { cb?.(); onClose(); } : cb;
  return (
    <>
      {/* {onImportCreditCard && (
        <button onClick={w(onImportCreditCard)} style={NAV_BTN}><CreditCard size={16} /> Import Credit Card</button>
      )}
      {onImportBank && (
        <button onClick={w(onImportBank)} style={NAV_BTN}><Landmark size={16} /> Import Bank Statement</button>
      )} */}
      {/* {onTaxSummary && (
        <button onClick={w(onTaxSummary)} style={NAV_BTN}><Receipt size={16} /> Tax Summary</button>
      )}
      {onFinancialSummary && (
        <button onClick={w(onFinancialSummary)} style={NAV_BTN}><TrendingUp size={16} /> Financial Summary</button>
      )}
      {onBusinessSummary && (
        <button onClick={w(onBusinessSummary)} style={NAV_BTN}><BarChart2 size={16} /> My Finances</button>
      )} */}
      <hr style={{ border: 'none', borderTop: '1px solid rgba(255,255,255,0.12)', margin: '2px 0' }} />

      {onReviewLinksClick && (
        <button onClick={w(onReviewLinksClick)}      style={NAV_BTN}><Link       size={16} /> ER Review Links</button>
      )}
      <hr style={{ border: 'none', borderTop: '1px solid rgba(255,255,255,0.12)', margin: '2px 0' }} />

      {onBillingClick && (
        <button onClick={w(onBillingClick)}          style={NAV_BTN}><CreditCard size={16} /> Plan & Billing</button>
      )}
      <hr style={{ border: 'none', borderTop: '1px solid rgba(255,255,255,0.12)', margin: '2px 0' }} />
      {onInstallClick && (
        <button onClick={w(onInstallClick)}          style={NAV_BTN}><Download   size={16} /> Install App</button>
      )}
      {onHelpClick && (
        <button onClick={w(onHelpClick)}             style={NAV_BTN}><HelpCircle size={16} /> Help</button>
      )}
      <button
        onClick={w(onPrefsClick)}
        style={NAV_BTN}>
          <Settings       size={16} /> Preferences
        </button>
      {user
        ? <button onClick={w(onLogout)} style={NAV_BTN}><LogOut size={16} /> Sign Out</button>
        : <button onClick={w(onLogin)}  style={NAV_BTN}><LogIn  size={16} /> Sign In</button>
      }
    </>
  );
}

export function VersionTag() {
  return (
    <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)', textAlign: 'center', paddingTop: 4 }}>
      v{pkg.version} {pkg.buildTime ? `(${pkg.buildTime})` : ''}
    </div>
  );
}

export function FinanceViewTopBar({
  user, tier, isAdmin, onAdminClick, setAuthScreen,
  onNewReport, onLoadClick, onExportClick, onPrefsClick,
  onCreateReviewLinkClick, onReviewLinksClick,
  onBillingClick, onInstallClick, onHelpClick, onLogout, onLogin,
  onImportCreditCard, onImportBank, onBusinessSummary, onFinancialSummary, onTaxSummary,
  onBookkeeperClick,
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const close = () => setMenuOpen(false);

  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, position: 'relative' }}>
      {/* Left: logo */}
      <button
        onClick={() => typeof setAuthScreen === 'function' && setAuthScreen('home')}
        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', gap: 4 }}
        title="Home"
      >
        <LogoMark size={22} />
        <Wordmark size={15} />
      </button>

      {/* Right: tier/admin badge + username dropdown + AI + help */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <TierAdminBadges tier={tier} isAdmin={isAdmin} onAdminClick={onAdminClick} />
        <button
          onClick={() => setMenuOpen(o => !o)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 3, padding: '4px 6px', borderRadius: 6 }}
        >
          <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)', fontWeight: 600 }}>
            {user ? (user.displayName || user.email) : 'Guest'}
          </span>
          <ChevronDown size={13} color="rgba(255,255,255,0.5)" />
        </button>
        {onBookkeeperClick && (
          <button
            onClick={onBookkeeperClick}
            title="MySpend AI"
            style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.25)', borderRadius: 8, padding: '5px 7px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
          >
            <Sparkles size={16} color="rgba(255,255,255,0.85)" />
          </button>
        )}
        {onHelpClick && (
          <button
            onClick={onHelpClick}
            title="Help"
            style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.25)', borderRadius: 8, padding: '5px 7px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
          >
            <HelpCircle size={16} color="rgba(255,255,255,0.85)" />
          </button>
        )}
      </div>

      {/* Dropdown */}
      <div onClick={close} style={{ position: 'fixed', inset: 0, zIndex: 199, pointerEvents: menuOpen ? 'all' : 'none' }} />
      <div style={{
        position: 'absolute', top: 30, right: 0,
        transform: menuOpen ? 'translateY(0)' : 'translateY(-10px)',
        opacity: menuOpen ? 1 : 0,
        pointerEvents: menuOpen ? 'all' : 'none',
        zIndex: 200, background: T.navy, borderRadius: 8, padding: 10,
        display: 'flex', flexDirection: 'column', gap: 8,
        minWidth: 200, boxShadow: '0 12px 40px rgba(0,0,0,0.35)',
        transition: 'opacity 0.5s ease, transform 0.5s ease',
        maxHeight: 'calc(100vh - 80px)', overflowY: 'auto',
      }}>
        <NavMenu
          onClose={close}
          user={user}
          onNewReport={onNewReport}
          onLoadClick={onLoadClick}
          onExportClick={onExportClick}
          onCreateReviewLinkClick={onCreateReviewLinkClick}
          onReviewLinksClick={onReviewLinksClick}
          onBillingClick={onBillingClick}
          onInstallClick={onInstallClick}
          onHelpClick={onHelpClick}
          onLogout={onLogout}
          onLogin={onLogin}
          onImportCreditCard={onImportCreditCard}
          onImportBank={onImportBank}
          onBusinessSummary={onBusinessSummary}
          onFinancialSummary={onFinancialSummary}
          onPrefsClick={onPrefsClick}
          onTaxSummary={onTaxSummary}
        />
        <VersionTag />
      </div>
    </div>
  );
}
